<template>
  <div>

    <Banner />

    <div class="container-fluid">
      <div class="container">

        <div class="course-search">
          <div v-if="displayingAll" class="course-search__label">Search for a course <strong>or</strong> browse the full list below...</div>
          <div v-else class="course-search__label">Search for your course</div>
          <div class="course-search__input-outer">
            <input class="course-search__input" type="text" v-model="searchString" v-on:keyup="debounce" v-on:keyup.enter="newSearch()" ref="searchString"
              placeholder="Search by keyword, code or topic" v-on:focus="searchFocused = true" v-on:blur="searchFocused = false" />
            <button class="course-search__btn-clear" type="button" v-if="searchString !== null && searchString !== ''" v-on:click="clearSearch()">
              <span class="course-search__icon far fa-times"></span>
            </button>
            <select v-model="searchFilterCourseLevel" class="course-search__filter" :class="filterOptionFade ? 'filter-fade' : ''"
              aria-label="Filter course levels" v-on:focus="filterFocused = true" v-on:blur="filterFocused = false" v-on:change="filterResults()">
              <option value="all">All study levels</option>
              <option v-for="type in courseTypes" v-bind:key="type.id"
                v-bind:value="type.courseloop">
                {{type.label}}
              </option>
            </select>
            <button class="course-search__btn-search" type="button" v-on:click="newSearch()" :disabled="searchString == null">
              <span class="course-search__icon far fa-search"></span>
            </button>
          </div>
          <div class="validation-error" v-if="!searchStringValid">
            <i class="far fa-exclamation-circle mr-1"></i> Please enter at least 3 alphanumeric characters when searching.
          </div>
        </div>

        <div class="row mb-2" v-if="!displayingAll">
          <div class="col-12">
            <div class="page-alert info w-100">
              <i class="page-alert__icon far fa-info-circle"></i>
              <div class="page-alert__body">
                <div class="page-alert__title">Unfortunately not all courses can be planned using the {{ __('label:app title') }} in the current version</div>
                <p>To find courses which are available for use with the {{ __('label:app title') }} look for this tick <i
                    class="whitelist-badge far fa-badge-check"></i>
                  or use the filter below.</p>
                <div class="custom-control custom-checkbox">
                  <input type="checkbox" v-model="filterWhitelist" id="filter-whitelist-checkbox" class="custom-control-input"
                    v-on:change="newSearch()">
                  <label class="custom-control-label text-left" for="filter-whitelist-checkbox">
                    Show {{ __('label:app title') }} ready courses only
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-if="loading">
          <div class="page-content-state page-content-state-loading col-12">
            <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
            <div class="page-content-state__content">Searching</div>
          </div>
        </div>

        <div class="row" v-if="!loading && resultData &&  resultData.length == 0">
          <div class="page-content-state col-12">
            <span class="page-content-state__icon fal fa-sad-cry"></span>
            <div class="page-content-state__header">No results found.</div>
            <div class="page-content-state__content">Sorry, we couldn't find any results for '{{ searchString }}'.<br /> Try another search.</div>
          </div>
        </div>

        <template v-if="!loading && resultData && resultData.length > 0">

          <div class="row">
            <template v-if="displayingAll">
              <div class="search-results-count col-lg-8 col-12">
                <h4>Browse all courses currently available on the Course Visualiser</h4>
              </div>
              <div class="col-lg-4 col-12 align-self-center mt-lg-0 mt-md-2">
                <span id="tooltip-whitelist-badge-key"><i class="whitelist-badge far fa-badge-check"></i> {{ __('label:app title') }} ready courses</span>
              </div>
              <b-tooltip target="tooltip-whitelist-badge-key" triggers="hover" placement="bottom">
                Unfortunately not all courses can be planned using the Course Visualiser in the current version.
                Courses marked with this tick can be planned using the {{ __('label:app title') }}.
                For other courses you will need to use the Handbook via the provided link.
              </b-tooltip>
            </template>
            <template v-else>
              <div class="search-results-count col-12">
                <h4>We found {{ filteredResults.length }} results</h4>
              </div>
            </template>
          </div>

          <div class="search-results row">
            <template v-for="(result, index) in filteredResults.slice((currentPage-1)*perPage, (currentPage-1)*perPage+perPage)">
              <CourseSearchResultCard :result="result" :key="`result-${index}`" @courseClick="updateSelection"></CourseSearchResultCard>
            </template>
          </div>

          <template v-if="!loading && filteredResults && filteredResults.length > perPage">
            <b-pagination class="condition-set__pagination"
              v-model="currentPage"
              :total-rows="filteredResults.length"
              :per-page="perPage"
              :disabled="loading"
              first-number
              last-number>
            </b-pagination>
          </template>

        </template>

      </div>
    </div>

    <b-modal id="modal-course-details" size="lg" hide-header hide-footer>

      <div class="modal-close-head">
        <button type="button" @click="$bvModal.hide('modal-course-details')" class="modal-close btn-clear"><i class="far fa-times"></i></button>
      </div>

      <!-- Loading state -->
      <template v-if="loadingCourseDetail">
        <div class="modal-state" :class="highlightedCourse ? 'modal-state-inline' : ''">
          <span class="modal-state__icon  far fa-spinner-third fa-spin"></span>
          <div class="modal-state__content">Loading</div>
        </div>
      </template>

      <!-- Content -->
      <template v-if="highlightedCourse">
        <div class="modal-course-head">
          <div class="modal-course-head__label-credits">Credits: {{ highlightedCourse.requiredCredits }}</div>
          <p class="modal-course-head__code"><i v-if="canGeneratePlan()" class="whitelist-badge far fa-badge-check ml-0"></i>{{ highlightedCourse.code }}</p>
          <div class="modal-course-head__title">
            <div v-if="isReviewer" class="float-right">
              <div v-if="!highlightedCourse.isActive" class="badge badge-primary">{{ statusLabel(highlightedCourse.status) }}</div>
              <div class="badge badge-secondary">Version {{ highlightedCourse.courseLoopVersionNumber }}</div>
            </div>
            {{highlightedCourse.name}}
          </div>
          <div class="modal-course-head__details">
            <dl>
              <dt>Enrolment pattern</dt>
              <dd><span v-for="(attendType, index) in highlightedCourse.attendanceTypes" :key="index">{{ attendType }}</span></dd>
            </dl>
            <dl>
              <dt>Student types</dt>
              <dd><span v-for="(studentType, index) in getStudentTypes" :key="index">{{ studentType }}</span></dd>
            </dl>
          </div>
        </div>
        <div v-html="highlightedCourse.description"></div>
        <div class="mt-3">
          <a :href="`https://api.murdoch.edu.au/handbook/v2/gotohandbook/${ highlightedCourse.code }`" class="link-highlight" target="_blank">
            Read more on this course in the handbook.
          </a>
        </div>

        <div v-if="isReviewer && !canGeneratePlan()" class="modal-body__controls mt-2" >
          <div class="page-alert warning">
            <div class="page-alert__icon">
              <i class="far fa-exclamation-circle"></i>
            </div>
            <div>
              <div class="page-alert__title">This course is not currently available in the {{ __('label:app title') }}.</div>
              <div class="page-alert__body" v-if="!hasValidOfferings">
                <p>This course is not currently available to staff or students in the Course Visualiser as there are no valid offerings for this course version. </p>
                <template v-if="!highlightedCourse.isActive || !highlightedCourse.isWhitelisted">
                  <p>Once this course has offerings, the course will still not be visible to students in the public Course Visualiser:</p>
                  <ul>
                    <li v-if="!highlightedCourse.isActive">This course version is in a proposed state and can only be viewed by staff for testing purposes.</li>
                    <li v-if="!highlightedCourse.isWhitelisted">This course has not been flagged in Courseloop for publication in the Course Visualiser.</li>
                  </ul>
                </template>
              </div>
              <div v-else class="page-alert__body">
                <p>This course is not visible to students in the public Course Visualiser. It is available to view by Murdoch staff only.</p>
                <ul>
                  <li v-if="!highlightedCourse.isActive">This course version is in a proposed state and can only be viewed by staff for testing purposes.</li>
                  <li v-if="!highlightedCourse.isWhitelisted">This course has not been flagged in Courseloop for publication in the Course Visualiser.</li>
                </ul>
              </div>
            </div>
          </div>

          <RefreshCourseDataButton v-if="isReviewer" class="float-right" :course="highlightedCourse" />

          <div v-if="hasValidOfferings">
            <button type="button" class="btn btn-primary float-left mr-4" v-on:click="confirmCourse">Test course</button>
            <span v-if="highlightedCourse.isActive" class="custom-control custom-checkbox float-left mt-1">
              <input type="checkbox" v-model="includeDraftVersions" id="chk-include-versions" class="custom-control-input"/>
              <label class="ml-1 mr-2 custom-control-label modal-course-control-label" for="chk-include-versions">Include proposed units/unit sets</label>
            </span>
          </div>
        </div>

        <!-- Whitelisted course actions -->
        <div class="modal-body__controls" v-else-if="canGeneratePlan()">
          <RefreshCourseDataButton v-if="isReviewer" class="float-right" :course="highlightedCourse" />
          <button type="button" class="btn btn-primary float-left mr-4" v-on:click="confirmCourse">Select course</button>
          <span v-if="isReviewer" class="custom-control custom-checkbox float-left mt-1">
            <input type="checkbox" v-model="includeDraftVersions" id="chk-include-versions" class="custom-control-input"/>
            <label class="ml-1 mr-2 custom-control-label modal-course-control-label" for="chk-include-versions">Include proposed units/unit sets</label>
          </span>
        </div>

        <!-- NON-Whitelisted course actions -->
        <div class="modal-body__controls" v-else>
          <div class="page-alert warning my-0">
            <div class="page-alert__icon">
              <i class="far fa-exclamation-circle"></i>
            </div>
            <div>
              <div class="page-alert__title">Sorry we are unable to create a course plan for this course.</div>
              <div class="page-alert__body">
                <p>Please contact the Academic Chair or visit the course plans page below for more information.</p>
                <dl class="value-pair" v-if="highlightedCourse.academicChair.length">
                  <dt>Academic chair</dt>
                  <dd>{{ highlightedCourse.academicChair[0].name }}<br />
                    <a class="link-highlight-grey"
                      :href="`mailto:${ highlightedCourse.academicChair[0].email }`">{{ highlightedCourse.academicChair[0].email }}</a>
                  </dd>
                </dl>
                <a href='https://www.murdoch.edu.au/mymurdoch/support-advice/student-admin/enrolment/how-to-enrol/course-plans'
                  target="_blank"
                  class="link-highlight-grey"
                  v-if="!canGeneratePlan()">Visit the myMurdoch Course plans page</a>
              </div>
            </div>
          </div>
        </div>

      </template>

    </b-modal>

  </div>
</template>

<style scoped lang="scss">
</style>

<script>
import useBaseStep from 'client/js/composition/base-step'
import Banner from './common/Banner.vue'
import CourseSearchResultCard from './course-search/CourseSearchResultCard.vue'
import RefreshCourseDataButton from './course-search/RefreshCourseDataButton.vue'
import Curriculum from 'client/js/models/curriculum.js'
import CourseTypes from 'common/models/course-types.js'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import errorModal from 'client/js/utils/error-modal'
import moment from 'moment'

export default {
  props: {
    pageSize: { type: Number, required: false }
  },
  setup(props, context) {
    return {
      baseStep: useBaseStep(props, context)
    }
  },
  data() {
    return {
      loading: false,
      searchString: '',
      searchStringValid: true,
      searchFilterCourseLevel: 'all',
      resultData: null,
      filteredResults: [],
      courseTypes: CourseTypes.filter((type) => type.showAsFilter),
      highlightedCourse: null,
      includeDraftVersions: false,
      loadingCourseDetail: false,
      currentPage: 1,
      perPage: 9,
      isWhitelisted: false,
      displayingAll: true,
      filterFocused: false,
      searchFocused: false,
      filterWhitelist: false,
      refreshingCourse: false,
      courseRefreshed: false
    }
  },
  methods: {
    ...mapMutations({
      selectCourse: 'courses/setSelectedCourseAndDrafts',
      setSearchParams: 'courses/setSearchCourseParams',

      resetPreferences: 'preferences/resetState',
      resetUnits: 'units/resetState',
      resetPlan: 'plans/resetState'
    }),
    ...mapActions({
      refreshCourse: 'courseUpdates/refreshCourse',
      checkCourseRefresh: 'courseUpdates/checkCourseRefresh'
    }),

    dateChecker: function (date) {
      return moment(date).isBefore(moment())
    },
    filterResults: function () {
      this.loading = true
      this.filteredResults = []
      if (this.searchFilterCourseLevel === 'all') {
        this.filteredResults = this.resultData
      } else {
        this.resultData.forEach((course) => {
          if (course.marketedCourseType.toLowerCase().includes(this.searchFilterCourseLevel.split(',')[0].toLowerCase())) {
            this.filteredResults.push(course)
          }
        })
      }
      this.loading = false
      this.displayingAll = false
    },
    debounce: function () {
      if (this.timeout) clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.newSearch()
      }, 1000)
    },
    newSearch: async function () {
      // if there is a timeout clear it (avoids double search)
      if (this.timeout) clearTimeout(this.timeout)
      // Basic input validation (short strings breaks the course search API)
      let searchString = (this.searchString || '').trim()
      this.searchStringValid = searchString.length > 2 && searchString.match(/^[\w-\s]+$/)
      if (!this.searchStringValid && this.searchString == '') {
        this.clearSearch()
        // this.getAllCourses()
        return
      } else if (!this.searchStringValid) {
        this.$refs.searchString.focus()
        this.resultData = null
        return
      }

      this.resultData = []
      this.currentPage = 1
      try {
        this.loading = true
        this.setSearchParams({
          searchString,
          filter: this.searchFilterCourseLevel
        })
        const response = await Curriculum.searchCourses(this.searchString, null, !this.isReviewer)
        this.resultData = response.courses.filter((unit) => !this.dateChecker(unit.endDate))

        if (this.filterWhitelist) {
          this.resultData = this.resultData.filter((course) => course.isWhitelisted)
        } else {
          this.resultData = this.resultData
        }
        this.filterResults()
      } catch (err) {
        this.$bvModal.hide('modal-course-details')
        await errorModal(this, 'server-error')
        this.$router.go()
      } finally {
        this.displayingAll = false
        this.loading = false
      }
    },
    getAllCourses: async function () {
      this.searchStringValid = true
      this.resultData = []
      this.currentPage = 1
      try {
        this.displayingAll = true
        this.loading = true
        const response = await Curriculum.courseList()
        this.resultData = response.courses.filter((unit) => !this.dateChecker(unit.endDate))
        this.filteredResults = this.resultData
      } catch (err) {
        this.$bvModal.hide('modal-course-details')
        await errorModal(this, 'server-error', err)
        this.$router.go()
      } finally {
        this.loading = false
      }
    },
    clearSearch() {
      this.searchString = null
      this.searchFilterCourseLevel = 'all'
      this.setSearchParams({
        searchString: ''
      })
      this.getAllCourses()
    },
    updateSelection: async function (id, version, isDraft) {
      this.loadingCourseDetail = true
      // console.log('Updating selection for course:', id, version, isDraft)
      try {
        // Default to loading draft dependencies for draft courses
        this.highlightedCourse = await Curriculum.getCourse(id, version, !!version && isDraft)
        // console.log('Updated course:', this.highlightedCourse.cacheTimestamp)
      } catch (err) {
        console.log(err)
        this.$bvModal.hide('modal-course-details')
        await errorModal(this, 'server-error')
        this.$router.go()
      } finally {
        this.loadingCourseDetail = false
      }
    },
    refreshCourseCache() {
      this.refreshCourse({courseId: this.highlightedCourse.code, version: this.highlightedCourse.courseLoopVersionNumber})
      this.refreshingCourse = true
    },
    confirmCourse() {
      // set course selection
      // TODO: This should call an Action, not a Mutation, so that Action can do other stuff (and be treated separate from other course mutations)
      let course = this.highlightedCourse
      let incDrafts = this.includeDraftVersions
      // console.log('Selected course:', course.code, course.name, this.includeDraftVersions)
      this.selectCourse({course, incDrafts})
      if (this.$gtm.enabled())
        this.$gtm.trackEvent({
          event: 'interaction',
          category: 'Select Course',
          action: 'click',
          label: 'Course Selected',
          value: `${course.code} ${course.name}`+(this.isReviewer?` Reviewing v${course.courseLoopVersionNumber} ${course.status}`:''),
          rest: {
            course: {
              code: course.code,
              name: course.name,
              version: course.courseLoopVersionNumber,
              status: course.status,
              isReviewer: this.isReviewer,
              incDrafts: this.includeDraftVersions
            }
          }
        })

      // empty out preferences
      this.resetPreferences()

      // empty out completedunits
      this.resetUnits()

      // empty out curriculum items
      this.resetPlan()

      this.baseStep.valid(true)
      this.baseStep.next()
    },
    statusLabel(id) {
      return this.__(`label:status ${id.toLowerCase()}`)
    },
    canGeneratePlan: function () {
      return this.highlightedCourse.validOfferings.length && this.isWhitelisted && this.highlightedCourse.isActive
    }
  },
  computed: {
    ...mapGetters({
      getSearchParams: 'courses/getSearchCourseParams',
      isReviewer: 'global/isReviewer',
      isAuthenticated: 'global/isAuthenticated',
      isUpdatingCourses: 'courseUpdates/isUpdatingCourses',
      isCourseUpdating: 'courseUpdates/isCourseUpdating'
    }),
    isCurrentCourseUpdating() {
      return this.highlightedCourse && this.isCourseUpdating(this.highlightedCourse.code)
    },
    hasValidOfferings() { return !!this.highlightedCourse.validOfferings.length },
    filterOptionFade() {
      if ((this.searchString != null && this.searchString != '') || this.searchFocused || this.filterFocused || !this.displayingAll) {
        return false
      }
      return true
    },
    getStudentTypes() {
      var availStudentTypes = []
      this.highlightedCourse.courseOfferings.forEach((offering) => {
        offering.studentTypes.forEach((type) => {
          if (!availStudentTypes.includes(type)) {
            availStudentTypes.push(type)
          }
        })
      })
      return availStudentTypes
    }
  },
  watch: {
    includeDraftVersions: function (inc_draft, prev) {
      if (inc_draft != prev) {
        // console.log('Include draft versions:', this.highlightedCourse, inc_draft, prev)
        this.updateSelection(this.highlightedCourse.id, this.highlightedCourse.courseLoopVersionNumber, inc_draft)
      }
    },
    highlightedCourse: function (val) {
      const code = val.code
      const queryFlag = this.$route.query.whitelist
      if (queryFlag) {
        console.log('Whitelist bypassed')
        this.isWhitelisted = true
        return
      }
      this.isWhitelisted = !!val.isWhitelisted
      if (this.isWhitelisted) console.log(`${code} FOUND in whitelist`)
      else console.log(`${code} NOT FOUND in whitelist`)
    },
    // refreshingCourse
    isCurrentCourseUpdating: function (refreshing, prev) {
      if (prev && !refreshing) {
        console.log('Course refreshed:', this.highlightedCourse.code)
        this.refreshingCourse = false
        this.updateSelection(this.highlightedCourse.id, this.highlightedCourse.courseLoopVersionNumber, !this.highlightedCourse.isActive)
        this.courseRefreshed = true
      }
    },
  },
  mounted: async function () {
    const params = this.getSearchParams
    this.searchString = params.searchString
    this.searchFilterCourseLevel = params.filter
    if (this.searchString) {
      await this.newSearch()
    }
    if (!this.searchString) {
      await this.getAllCourses()
    }
  },
  destroyed() {},
  components: { CourseSearchResultCard, Banner, RefreshCourseDataButton }
}
</script>

<style>
</style>
